/* PAN HOMZ — real Shopify variant selection; show the matching side when
 * both directions share a single image, without altering order variant IDs. */
(() => {
  'use strict';

  const cache = new WeakMap();
  const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

  function variantData(picker) {
    if (cache.has(picker)) return cache.get(picker);
    let variants = [];
    try {
      variants = JSON.parse(picker.querySelector('.pan-card-variant-json')?.textContent || '[]');
    } catch (err) {
      console.warn('PAN HOMZ: invalid catalog variant data', err);
    }
    cache.set(picker, variants);
    return variants;
  }

  function setPressed(buttons, datasetKey, selected) {
    buttons.forEach((button) => {
      const active = button.dataset[datasetKey] === selected;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function writePrice(card, variant) {
    const priceBox = card.querySelector('.pan-card-prices');
    if (!priceBox) return;
    const current = priceBox.querySelector('.pan-card-current');
    if (!current) return;
    const price = Number(variant.price) || 0;
    const previous = Number(variant.compareAt) || 0;
    const currency = current.querySelector('.pan-card-currency')?.textContent?.trim() || 'EGP';
    const currencyElement = document.createElement('small');
    currencyElement.className = 'pan-card-currency';
    currencyElement.textContent = currency;
    current.replaceChildren(document.createTextNode(`${numberFormatter.format(price / 100)} `), currencyElement);

    let old = priceBox.querySelector('.pan-card-old');
    let discount = priceBox.querySelector('.pan-card-discount');
    const onSale = previous > price;
    if (onSale) {
      if (!old) {
        old = document.createElement('s');
        old.className = 'pan-card-old';
        priceBox.appendChild(old);
      }
      old.textContent = numberFormatter.format(previous / 100);
      if (!discount) {
        discount = document.createElement('bdi');
        discount.className = 'pan-card-discount';
        discount.dir = 'ltr';
        priceBox.appendChild(discount);
      }
      discount.textContent = `-${Math.round((previous - price) * 100 / previous)}%`;
    } else {
      old?.remove();
      discount?.remove();
    }
    const priceContainer = priceBox.closest('.price');
    priceContainer?.classList.toggle('price--on-sale', onSale);
    priceContainer?.classList.toggle('price--sold-out', !variant.available);
  }

  // Keep the current picture on screen while another shade downloads.
  // An eagerly fetched replacement also loads when the image sits above the
  // visible viewport and its original Shopify markup was loading="lazy".
  function showPhoto(card, variant, isInitial = false) {
    const photo = card.querySelector('.card__media .media img:first-of-type');
    const note = card.querySelector('.pan-card-photo-warning');
    if (!photo) return;
    if (!photo.dataset.panCoverSrc) {
      photo.dataset.panCoverSrc = photo.getAttribute('src') || '';
      photo.dataset.panCoverSrcset = photo.getAttribute('srcset') || '';
      photo.dataset.panCoverAlt = photo.getAttribute('alt') || '';
    }

    const token = String(Number(photo.dataset.panImageRequest || 0) + 1);
    photo.dataset.panImageRequest = token;
    photo.hidden = false;
    photo.style.visibility = 'visible';
    photo.style.opacity = '1';
    // The catalog cover is already present in server-rendered HTML. Do not
    // eagerly download every option image at page load, including offscreen
    // product cards. Only customer-initiated switches need eager downloads.
    if (isInitial) {
      if (note) note.hidden = true;
      return;
    }
    photo.setAttribute('loading', 'eager');

    function setImage(url, alt, coverSrcset = '') {
      if (photo.dataset.panImageRequest !== token) return;
      if (coverSrcset) photo.setAttribute('srcset', coverSrcset);
      else photo.removeAttribute('srcset');
      photo.setAttribute('src', url);
      photo.setAttribute('alt', alt);
    }

    function restoreCover() {
      setImage(photo.dataset.panCoverSrc, photo.dataset.panCoverAlt, photo.dataset.panCoverSrcset);
      if (note) note.hidden = isInitial;
    }

    if (!variant.image) {
      restoreCover();
      return;
    }
    if (note) note.hidden = true;
    const url = variant.image;
    const alt = variant.imageAlt || 'صورة المنتج للون المختار';

    // No download or visual flash for already displayed/cached photos.
    if (photo.getAttribute('src') === url && !photo.getAttribute('srcset')) {
      setImage(url, alt);
      return;
    }
    if (typeof Image !== 'function') {
      // Minimal/no-image environments still update directly.
      setImage(url, alt);
      return;
    }
    const next = new Image();
    next.onload = () => {
      if (photo.dataset.panImageRequest !== token) return;
      photo.onerror = () => {
        photo.onerror = null;
        restoreCover();
      };
      setImage(url, alt);
    };
    next.onerror = () => {
      if (photo.dataset.panImageRequest !== token) return;
      restoreCover();
    };
    // Starting this request eagerly (not loading=lazy) handles switching
    // colors from the lower half of product cards on mobile and desktop.
    next.src = url;
  }

  function applyDirectionPreview(picker, selectedVariant) {
    const card = picker.closest('.pan-product-card');
    if (!card) return;
    const warning = picker.querySelector('.pan-card-direction-warning');
    const index = Number(picker.dataset.directionIndex);
    const variants = variantData(picker);
    const selectedValue = String(selectedVariant.options?.[index] || '').trim().toLowerCase();
    const selectedLeft = ['شمال', 'يسار', 'left'].includes(selectedValue);
    const selectedRight = ['يمين', 'right'].includes(selectedValue);
    const originalLeft = picker.dataset.panOriginalDirection === 'left';
    const opposite = index >= 0 ? variants.find((v) =>
      String(v.id) !== String(selectedVariant.id) &&
      v.options?.[index] !== selectedVariant.options?.[index] &&
      v.options?.every((value, i) => i === index || value === selectedVariant.options[i])
    ) : null;
    const shared = Boolean(selectedVariant.image && opposite?.image && selectedVariant.image === opposite.image);
    const shouldMirror = shared && (selectedLeft || selectedRight) && selectedLeft !== originalLeft;
    card.classList.toggle('pan-card-chaise-mirrored', shouldMirror);
    if (warning) warning.hidden = !shouldMirror;
  }

  function chooseVariant(picker, color, direction) {
    const variants = variantData(picker);
    const colorIndex = Number(picker.dataset.colorIndex);
    const directionIndex = Number(picker.dataset.directionIndex);
    const matchingColor = colorIndex >= 0
      ? variants.filter((variant) => variant.options?.[colorIndex] === color)
      : variants;
    if (!matchingColor.length) return null;
    // Prioritize the exact color/direction combination, never silently choose an unrelated color.
    if (directionIndex >= 0 && direction != null) {
      return matchingColor.find((variant) => variant.options[directionIndex] === direction) ||
        matchingColor.find((variant) => variant.available) || matchingColor[0];
    }
    return matchingColor.find((variant) => variant.available) || matchingColor[0];
  }

  function updateCard(picker, variant, isInitial = false) {
    const card = picker.closest('.pan-product-card');
    if (!card || !variant) return;
    const colorIndex = Number(picker.dataset.colorIndex);
    const directionIndex = Number(picker.dataset.directionIndex);
    const selectedColor = colorIndex >= 0 ? variant.options[colorIndex] : null;
    const selectedDirection = directionIndex >= 0 ? variant.options[directionIndex] : null;

    setPressed(picker.querySelectorAll('[data-pan-color]'), 'panColor', selectedColor);
    setPressed(picker.querySelectorAll('[data-pan-direction]'), 'panDirection', selectedDirection);
    picker.dataset.initialVariant = String(variant.id);
    const href = `${picker.dataset.baseUrl}${picker.dataset.baseUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
    card.querySelectorAll('a.pan-card-details, .card__heading a').forEach((a) => { a.href = href; });
    showPhoto(card, variant, isInitial);
    applyDirectionPreview(picker, variant);
    writePrice(card, variant);
  }

  function initializeCards(root = document) {
    root.querySelectorAll('.pan-card-variant-picker').forEach((picker) => {
      if (picker.dataset.panInitialized === 'true') return;
      picker.dataset.panInitialized = 'true';
      const variants = variantData(picker);
      const initial = variants.find((variant) => String(variant.id) === picker.dataset.initialVariant);
      if (initial) updateCard(picker, initial, true);
    });
  }

  document.addEventListener('shopify:section:load', (event) => initializeCards(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeCards(), { once: true });
  } else {
    initializeCards();
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('.pan-card-variant-picker button[data-pan-color], .pan-card-variant-picker button[data-pan-direction]');
    if (!control) return;
    const picker = control.closest('.pan-card-variant-picker');
    const variants = variantData(picker);
    if (!variants.length) return;
    const initial = variants.find((variant) => String(variant.id) === picker.dataset.initialVariant) || variants[0];
    const colorIndex = Number(picker.dataset.colorIndex);
    const directionIndex = Number(picker.dataset.directionIndex);
    const color = control.dataset.panColor ?? (colorIndex >= 0 ? initial.options[colorIndex] : null);
    const direction = control.dataset.panDirection ?? (directionIndex >= 0 ? initial.options[directionIndex] : null);
    const variant = chooseVariant(picker, color, direction);
    updateCard(picker, variant);
  });
})();
